import { confirmCloseTaskDrafts } from './task-drafts';
import { EmptyState } from '@/components/EmptyState';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Plus, SquareKanban, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TaskBoard, TaskCard } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/usePermission';
import { useTaskStore } from '@/stores/taskStore';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { isSnoozed } from '@/lib/snooze';
import { NewTaskDialog, type DraftContext } from './NewTaskDialog';
import { TaskCardView } from './TaskCardView';
import { TaskDetailView } from './TaskDetailView';
import { openTasksFor, tasksPanelHiddenOn, usePageTaskScope, usePageTasks } from './usePageTasks';
import { attachFilesToTask } from './task-files';

/**
 * The Tasks panel (opened from the foot of the sidebar): the tasks on whatever you're looking at,
 * with quick-add pre-linked to it. The panel doesn't cover the page, so you can fix the thing
 * with the conversation beside it.
 */
export function TasksPanelHost() {
  const canView = usePermission('TASKS');
  const teamActive = useAuthStore((s) => s.user?.active_team?.status === 'active');
  const location = useLocation();
  const hidden = !canView || !teamActive || tasksPanelHiddenOn(location.pathname);
  const pageScope = usePageTaskScope();
  const panel = useTaskStore((s) => s.panel);
  const setPanel = useTaskStore((s) => s.setPanel);
  const { board } = usePageTasks(!hidden);
  const [newOpen, setNewOpen] = useState(false);

  // A different page: drop any row focus and open task from the last one.
  useEffect(() => {
    setPanel({ focus: null, taskId: null });
  }, [location.pathname, setPanel]);

  const doneIds = useMemo(() => new Set(board?.columns.filter((c) => c.is_done).map((c) => c.id)), [board]);
  const tasks = board?.tasks ?? [];
  const focusMatch = (t: TaskCard) =>
    !panel.focus || t.contexts.some((c) => c.type === panel.focus!.type && c.id === panel.focus!.id);
  const open = openTasksFor(tasks, doneIds, panel.focus);
  const finished = tasks.filter((t) => (t.archived || doneIds.has(t.column_id)) && focusMatch(t));
  const snoozed = tasks.filter((t) => !t.archived && !doneIds.has(t.column_id) && isSnoozed(t.snoozed_until) && focusMatch(t));

  if (hidden) return null;

  // On a batch page the page's scope is the batch: a new task on one of its products should
  // show in this panel straight away, so the draft carries that parent.
  const scopeParent = pageScope.scope[0];
  const withParent = (ref: { type: DraftContext['type']; id: string }) =>
    scopeParent && !(scopeParent.type === ref.type && scopeParent.id === ref.id) && scopeParent.type !== 'page'
      ? scopeParent
      : null;
  const primary: DraftContext = panel.focus
    ? { ...panel.focus, url: '', parent: withParent(panel.focus) }
    : { ...pageScope.primary, title: pageScope.title, url: '', parent: withParent(pageScope.primary) };

  return (
    <>
      {/* Opened from the foot of the sidebar (SidebarTasksButton). */}
      {panel.open ? (
        <aside
          className={cn(
            'fixed bottom-0 right-0 top-0 z-40 flex flex-col border-l bg-background shadow-2xl transition-[width]',
            panel.taskId ? 'w-full sm:w-[36rem]' : 'w-full sm:w-[26rem]'
          )}
          aria-label="Tasks panel"
        >
          <div className="flex items-center gap-2 border-b px-4 py-3">
            {panel.taskId ? (
              <Button variant="ghost" size="icon-compact" aria-label="Back to the list" onClick={() => void confirmCloseTaskDrafts().then((ok) => ok && setPanel({ taskId: null }))}>
                <ArrowLeft className="size-4" />
              </Button>
            ) : null}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-sm font-semibold">
                {panel.taskId ? 'Task' : panel.focus ? `Tasks · ${panel.focus.title}` : 'Tasks on this page'}
              </h2>
            </div>
            <Button variant="ghost" size="icon-compact" asChild>
              <Link to="/tasks" aria-label="Open the Board" data-hint="Open the Board">
                <SquareKanban className="size-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon-compact" aria-label="Close" onClick={() => void confirmCloseTaskDrafts().then((ok) => ok && setPanel({ open: false, taskId: null }))}>
              <X className="size-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {panel.taskId && board ? (
              <TaskDetailView
                taskId={panel.taskId}
                meta={board}
                layout="narrow"
                currentPage={{ ...pageScope.primary, title: pageScope.title }}
              />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button size="sm" disabled={!board} onClick={() => setNewOpen(true)}>
                    <Plus className="size-4" />
                    New task
                  </Button>
                  {panel.focus ? (
                    <Button variant="ghost" size="sm" onClick={() => setPanel({ focus: null })}>
                      Show the whole page
                    </Button>
                  ) : null}
                </div>
                {!board ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : open.length === 0 ? (
                  <EmptyState size="compact" title="No open tasks here" />
                ) : (
                  open.map((task) => (
                    <TaskCardView
                      key={task.id}
                      task={task}
                      members={board.members}
                      labels={board.labels}
                      types={board.types}
                      onClick={() => setPanel({ taskId: task.id })}
                      onKeyDown={(e) => e.key === 'Enter' && setPanel({ taskId: task.id })}
                      onDropFiles={(files) => void attachFilesToTask(task.id, files)}
                    />
                  ))
                )}
                {board ? (
                  <>
                    <FoldedCards label="Snoozed" tasks={snoozed} board={board} onOpen={(taskId) => setPanel({ taskId })} />
                    <FoldedCards label="Done" tasks={finished} board={board} onOpen={(taskId) => setPanel({ taskId })} />
                  </>
                ) : null}
              </div>
            )}
          </div>
        </aside>
      ) : null}
      {board ? (
        <NewTaskDialog
          open={newOpen || Boolean(panel.create)}
          onOpenChange={(next) => {
            setNewOpen(next);
            if (!next && panel.create) setPanel({ create: false, draft: null });
          }}
          meta={board}
          initialContexts={[panel.draft?.context ? { ...panel.draft.context, url: '', parent: withParent(panel.draft.context) } : primary]}
          initialTitle={panel.draft?.title}
          initialText={panel.draft?.text}
          currentPage={primary}
          onCreated={(taskId) => setPanel({ taskId })}
        />
      ) : null}
    </>
  );
}

/** Snoozed or finished tasks under the open ones, folded away until clicked. */
function FoldedCards({
  label,
  tasks,
  board,
  onOpen,
}: {
  label: string;
  tasks: TaskCard[];
  board: Pick<TaskBoard, 'members' | 'labels' | 'types'>;
  onOpen: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!tasks.length) return null;
  return (
    <div className="space-y-2 border-t pt-3">
      <button
        type="button"
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        {label} · {tasks.length}
      </button>
      {open
        ? tasks.map((task) => (
            <TaskCardView
              key={task.id}
              task={task}
              members={board.members}
              labels={board.labels}
              types={board.types}
              className="opacity-70"
              onClick={() => onOpen(task.id)}
            />
          ))
        : null}
    </div>
  );
}
